import { useEffect } from 'react';
import { clamp, prefersReducedMotion, watchReducedMotion } from './reducedMotion';

const SELECTOR = '[data-tilt]';

/**
 * One delegated pointer listener gives every `data-tilt` card a real perspective tilt,
 * instead of each card paying for its own listener and rAF loop. Pointer-only: coarse
 * pointers get the flat layout, which is what a finger-driven UI should do anyway.
 */
export function useTiltSurfaces() {
  useEffect(() => {
    let detach: (() => void) | null = null;

    const bind = () => {
      let active: HTMLElement | null = null;
      let frame = 0;
      let nextX = 0;
      let nextY = 0;

      const paint = () => {
        frame = 0;
        if (!active) return;
        active.style.setProperty('--tilt-x', nextY.toFixed(3));
        active.style.setProperty('--tilt-y', nextX.toFixed(3));
        active.style.setProperty('--glare-x', `${(nextX * 0.5 + 0.5) * 100}%`);
        active.style.setProperty('--glare-y', `${(nextY * 0.5 + 0.5) * 100}%`);
      };

      const release = (node: HTMLElement) => {
        node.classList.remove('is-tilting');
        node.style.setProperty('--tilt-x', '0');
        node.style.setProperty('--tilt-y', '0');
      };

      const handleMove = (event: PointerEvent) => {
        if (event.pointerType === 'touch') return;
        const target = event.target instanceof Element ? event.target.closest<HTMLElement>(SELECTOR) : null;
        if (target !== active) {
          if (active) release(active);
          active = target;
          if (active) active.classList.add('is-tilting');
        }
        if (!active) return;
        const rect = active.getBoundingClientRect();
        nextX = clamp(((event.clientX - rect.left) / rect.width - 0.5) * 2);
        nextY = clamp(((event.clientY - rect.top) / rect.height - 0.5) * 2);
        if (!frame) frame = requestAnimationFrame(paint);
      };

      const handleLeaveWindow = () => {
        if (active) release(active);
        active = null;
      };

      document.addEventListener('pointermove', handleMove, { passive: true });
      document.addEventListener('pointerleave', handleLeaveWindow, { passive: true });
      window.addEventListener('blur', handleLeaveWindow);

      return () => {
        document.removeEventListener('pointermove', handleMove);
        document.removeEventListener('pointerleave', handleLeaveWindow);
        window.removeEventListener('blur', handleLeaveWindow);
        if (frame) cancelAnimationFrame(frame);
        if (active) release(active);
      };
    };

    const sync = (reduced: boolean) => {
      detach?.();
      detach = null;
      if (reduced || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      detach = bind();
    };

    sync(prefersReducedMotion());
    const unwatch = watchReducedMotion(sync);

    return () => {
      unwatch();
      detach?.();
    };
  }, []);
}
